import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobAccountPageShell } from './MobAccountPageShell';
import { fetchVipProfile } from '../../utils/vipApi';

export function MobVip() {
    const navigate = useNavigate();
    const [animateIn, setAnimateIn] = useState(false);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isGuest =
        localStorage.getItem('guestUser') === 'true' || !localStorage.getItem('token');

    const spend = profile?.annualSpend ?? 0;
    const activeTier = profile?.activeTier;
    const nextTier = profile?.nextTier ?? null;
    const maxThreshold = profile?.maxThreshold ?? 0;
    const progressPct = profile?.progressPct ?? 0;
    const remaining = profile?.remaining ?? 0;
    const tierSteps = profile?.tierSteps ?? [];
    const benefits = profile?.benefits ?? [];
    const upcomingPerks = profile?.upcomingPerks ?? [];
    const milestoneTiers = profile?.tiers ?? [];

    const youPinPct = Math.min(92, Math.max(8, progressPct));

    useEffect(() => {
        const id = window.requestAnimationFrame(() => setAnimateIn(true));
        return () => window.cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (isGuest) {
                if (!cancelled) {
                    setProfile(null);
                    setLoading(false);
                }
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const data = await fetchVipProfile();
                if (!cancelled) setProfile(data);
            } catch (_e) {
                if (!cancelled) {
                    setError('Could not load VIP status. Please try again.');
                    setProfile(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isGuest]);

    return (
        <MobAccountPageShell title="VIP">
            {isGuest && (
                <section className="mob-account-card mob-vip-guest-banner">
                    <p className="mob-vip-guest-banner__text">
                        Sign in to track your VIP tier and yearly spend from delivered orders.
                    </p>
                    <button
                        type="button"
                        className="mob-account-cta mob-account-cta--inline"
                        onClick={() => navigate('/login')}
                    >
                        Sign in
                    </button>
                </section>
            )}

            <section className="mob-account-hero mob-account-hero--vip" aria-label="VIP membership">
                <p className="mob-account-hero__label">Your membership</p>
                <p className="mob-account-hero__value">
                    {loading ? '…' : activeTier ? `${activeTier.name} VIP` : 'VIP'}
                </p>
                <p className="mob-account-hero__sub">
                    {loading
                        ? 'Loading spend…'
                        : `₹${spend.toLocaleString('en-IN')} delivered spend this year`}
                </p>
                {!loading && !isGuest && (
                    <span className="mob-account-hero__badge">
                        <i className={`bi ${activeTier.icon}`} aria-hidden />
                        Active member
                    </span>
                )}
            </section>

            {error && (
                <p className="mob-vip-error" role="status">
                    {error}
                </p>
            )}

            {!isGuest && profile && maxThreshold > 0 && (
            <section className="mob-account-card mob-vip-track-card">
                <h2 className="mob-account-card__title">
                    <i className="bi bi-graph-up-arrow" aria-hidden />
                    Membership journey
                </h2>

                <div
                    className={`mob-vip-track${animateIn && !loading ? ' mob-vip-track--animated' : ''}`}
                    aria-label="VIP tier progress"
                    aria-busy={loading}
                >
                    <div className="mob-vip-track__rail-area">
                        <div
                            className="mob-vip-track__you-pin"
                            style={{ left: `${youPinPct}%` }}
                        >
                            You · ₹{spend.toLocaleString('en-IN')}
                        </div>
                        <div className="mob-vip-track__rail">
                            <div
                                className="mob-vip-track__fill"
                                style={{ width: animateIn && !loading ? `${progressPct}%` : '0%' }}
                                role="progressbar"
                                aria-valuenow={Math.round(progressPct)}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            />
                            {milestoneTiers.slice(0, -1).map((tier) => (
                                <span
                                    key={tier.id}
                                    className="mob-vip-track__milestone"
                                    style={{ left: `${(tier.threshold / maxThreshold) * 100}%` }}
                                    aria-hidden
                                />
                            ))}
                        </div>
                        <div className="mob-vip-track__scale">
                            <span>₹0</span>
                            <span>₹{maxThreshold.toLocaleString('en-IN')}</span>
                        </div>
                    </div>

                    <div className="mob-vip-track__tiers">
                        {tierSteps.map((step) => (
                            <div
                                key={step.id}
                                className={`mob-vip-track__tier mob-vip-track__tier--${step.status} mob-vip-track__tier--${step.name.toLowerCase()}`}
                                style={{
                                    '--tier-accent': step.accent,
                                    '--tier-accent-soft': step.accentSoft,
                                }}
                            >
                                <div className="mob-vip-track__dot">
                                    <i className={`bi ${step.icon}`} aria-hidden />
                                </div>
                                <span className="mob-vip-track__tier-name">{step.name}</span>
                                <span className="mob-vip-track__tier-threshold">
                                    ₹{step.threshold.toLocaleString('en-IN')}+
                                </span>
                            </div>
                        ))}
                    </div>

                    <p className="mob-vip-track__hint">
                        {loading ? (
                            'Calculating progress from your orders…'
                        ) : nextTier ? (
                            <>
                                <strong>₹{remaining.toLocaleString('en-IN')}</strong> more to unlock{' '}
                                <span className="mob-vip-track__hint-tier">{nextTier.name}</span> perks
                            </>
                        ) : (
                            <>You&apos;ve reached the highest tier — enjoy all Platinum benefits.</>
                        )}
                    </p>
                </div>
            </section>
            )}

            {profile && activeTier && (
            <section className="mob-account-card">
                <h2 className="mob-account-card__title">
                    <i className="bi bi-stars" aria-hidden />
                    Your {activeTier.name} benefits
                </h2>
                {benefits.map((b) => (
                    <div key={b.title} className="mob-vip-benefit">
                        <div className="mob-vip-benefit__icon" aria-hidden>
                            <i className={`bi ${b.icon}`} />
                        </div>
                        <div>
                            <p className="mob-vip-benefit__title">{b.title}</p>
                            <p className="mob-vip-benefit__desc">{b.description}</p>
                        </div>
                    </div>
                ))}
            </section>
            )}

            {profile && nextTier && upcomingPerks.length > 0 && (
                <section
                    className={`mob-vip-upcoming${animateIn && !loading ? ' mob-vip-upcoming--visible' : ''}`}
                    aria-labelledby="vip-upcoming-heading"
                >
                    <div className="mob-vip-upcoming__header">
                        <div className="mob-vip-upcoming__header-icon" style={{ '--tier-accent': nextTier.accent }}>
                            <i className={`bi ${nextTier.icon}`} aria-hidden />
                        </div>
                        <div>
                            <h2 id="vip-upcoming-heading" className="mob-vip-upcoming__title">
                                Unlock {nextTier.name}
                            </h2>
                            <p className="mob-vip-upcoming__subtitle">
                                Perks waiting when you reach ₹{nextTier.threshold.toLocaleString('en-IN')}+
                            </p>
                        </div>
                    </div>

                    <div className="mob-vip-upcoming__grid">
                        {upcomingPerks.map((perk, index) => (
                            <article
                                key={perk.title}
                                className="mob-vip-upcoming__card"
                                style={{ '--stagger': index }}
                            >
                                <div className="mob-vip-upcoming__card-lock" aria-hidden>
                                    <i className="bi bi-lock-fill" />
                                </div>
                                <div className="mob-vip-upcoming__card-icon">
                                    <i className={`bi ${perk.icon}`} aria-hidden />
                                </div>
                                <h3 className="mob-vip-upcoming__card-title">{perk.title}</h3>
                                <p className="mob-vip-upcoming__card-desc">{perk.description}</p>
                                <span className="mob-vip-upcoming__card-tag">{nextTier.name} exclusive</span>
                            </article>
                        ))}
                    </div>
                </section>
            )}

            <button type="button" className="mob-account-cta" onClick={() => navigate('/')}>
                Explore products
            </button>
        </MobAccountPageShell>
    );
}
